// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** Collection holding one document per case and retrieval requirement. */
export const CONTROLLED_TESTING_REGISTRY_RECORDS_COLLECTION = 'grc_controlled_testing_registry_records';
/** The `register_surface` an installed pack form declares to feed the registry vocabulary. */
export const CONTROLLED_TESTING_REGISTRY_REGISTER_SURFACE = 'controlled_testing_registry';
/** The field ids the surface's template carries the outcome codes and the track options under. */
export const CONTROLLED_TESTING_REGISTRY_FIELDS = {
    outcome: 'registry_outcome',
    track: 'dossier_track',
};
/** The evidence owner type a supplement's file is held under: owned by one registry row. */
export const CONTROLLED_TESTING_REGISTRY_EVIDENCE_OWNER_TYPE = 'controlled_testing_registry_evidence';
/**
 * The roles a registry row answers. A row names the requirement the installed pack tags with
 * the role (`controlled_testing_role`); the platform reads its title and article reference from
 * the installed framework and nothing else. The requirement's own id is stored on the row, as
 * the pack spells it, and is never a platform literal (Slice G, G2).
 */
export const CONTROLLED_TESTING_REGISTRY_REQUIREMENTS = {
    criminalRecord: 'criminal_record_retrieval',
    credential: 'credential_retrieval',
    corporate: 'corporate_retrieval',
};
/** Where a document comes from: the State Bank's retrieval, or a copy the organisation supplies (D1). */
export const CONTROLLED_TESTING_REGISTRY_SOURCES = [
    'registry_retrieval',
    'applicant_copy',
];
/**
 * The success classes a pack may declare on an outcome option (`outcome_class`). Only
 * `retrieved` is a success. Any other class needs a supplement or a recorded decision (D3). A
 * class outside this list is not read, so the option is not offered.
 */
export const CONTROLLED_TESTING_REGISTRY_OUTCOME_CLASSES = [
    'retrieved',
    'not_retrieved',
    'incomplete_or_inaccurate',
];
/** The option attributes the pack declares, read as the pack authored them. */
export const CONTROLLED_TESTING_REGISTRY_OPTION_ATTRIBUTES = {
    outcomeClass: 'outcome_class',
    appliesToRequirementIds: 'applies_to_requirement_ids',
    appliesToApplicationFormIds: 'applies_to_application_form_ids',
    investmentRegistrationLimb: 'investment_registration_limb',
};
/** The supplement's reference and the decision text are free text, bounded. */
export const CONTROLLED_TESTING_REGISTRY_REFERENCE_MAX = 500;
export const CONTROLLED_TESTING_REGISTRY_DECISION_TEXT_MAX = 1000;
/** Error codes the registry routes return. Stable, machine-readable names. */
export const CONTROLLED_TESTING_REGISTRY_ERROR_CODES = {
    OUTCOME_CORRECTION_INVALID: 'REGISTRY_OUTCOME_CORRECTION_INVALID',
    OUTCOME_DATE_REQUIRED: 'REGISTRY_OUTCOME_DATE_REQUIRED',
    SCOPE_NOT_DECLARED: 'REGISTRY_SCOPE_NOT_DECLARED',
    SCOPE_NOT_READY: 'REGISTRY_SCOPE_NOT_READY',
    SCOPE_MIGRATION_BLOCKED: 'REGISTRY_SCOPE_MIGRATION_BLOCKED',
    PERSONNEL_OUTCOME_READ_ONLY: 'REGISTRY_PERSONNEL_OUTCOME_READ_ONLY',
    PERSONNEL_LINK_IMMUTABLE: 'REGISTRY_PERSONNEL_LINK_IMMUTABLE',
    PERSONNEL_LINK_INVALID: 'REGISTRY_PERSONNEL_LINK_INVALID',
    PERSONNEL_LINK_REQUIRED: 'REGISTRY_PERSONNEL_LINK_REQUIRED',
    FOLLOW_UP_NOT_DECLARED: 'REGISTRY_FOLLOW_UP_NOT_DECLARED',
    FOLLOW_UP_EVIDENCE_REQUIRED: 'REGISTRY_FOLLOW_UP_EVIDENCE_REQUIRED',
    NOT_FOUND: 'REGISTRY_RECORD_NOT_FOUND',
    ALREADY_RECORDED: 'REGISTRY_RECORD_ALREADY_RECORDED',
    /** The requirement is not declared by an installed framework for this case. */
    REQUIREMENT_NOT_DECLARED: 'REGISTRY_REQUIREMENT_NOT_DECLARED',
    /** An outcome code the installed pack does not declare for this requirement, in force on the date. */
    OUTCOME_NOT_DECLARED: 'REGISTRY_OUTCOME_NOT_DECLARED',
    /** A State Bank outcome is recorded only on a document whose source is the registry. */
    OUTCOME_NEEDS_REGISTRY_SOURCE: 'REGISTRY_OUTCOME_NEEDS_REGISTRY_SOURCE',
    /** The supplement's file is not held for this row; upload it against the row first. */
    EVIDENCE_NOT_OWNED: 'REGISTRY_EVIDENCE_NOT_OWNED',
    /** A date the row records (seen, supplied or decided) is later than the organisation's today. */
    DATE_IN_FUTURE: 'REGISTRY_DATE_IN_FUTURE',
};
//# sourceMappingURL=controlled-testing-registry.js.map